/**
 * Movimientos de almacén.
 *
 * Es el histórico que da la **trazabilidad por lote** que exige el sector:
 * de qué lote salió el agente extintor que se cargó en qué equipo, en qué
 * centro y de qué cliente. Por eso la tabla es append-only y aquí sólo se
 * consulta.
 */

import { useSearchParams } from 'react-router-dom';
import { DataTable, type Column } from '../table/data-table.js';
import { useListing } from '../table/use-listing.js';
import { dayOf, quantity } from '../table/format.js';

interface Row {
  id: string;
  movement_kind: string;
  quantity: string;
  unit_cost: string | null;
  product_code: string;
  product_name: string;
  unit: string;
  warehouse_name: string;
  lot_id: string | null;
  work_order_number: string | null;
  customer_name: string | null;
  created_at: string;
}

const KIND: Record<string, { label: string; tone: string }> = {
  ENTRADA: { label: 'Entrada', tone: 'ok' },
  CONSUMO: { label: 'Consumo', tone: 'info' },
  SALIDA: { label: 'Salida', tone: 'info' },
  TRASPASO: { label: 'Traspaso', tone: 'off' },
  AJUSTE: { label: 'Ajuste', tone: 'warn' },
  DEVOLUCION: { label: 'Devolución', tone: 'ok' },
};

const COLUMNS: Column<Row>[] = [
  {
    id: 'createdAt',
    label: 'Fecha',
    render: (r) => dayOf(r.created_at),
    sortable: true,
    width: '8rem',
  },
  {
    id: 'movementKind',
    label: 'Tipo',
    width: '9rem',
    sortable: true,
    render: (r) => {
      const k = KIND[r.movement_kind] ?? { label: r.movement_kind, tone: 'off' };
      return <span className={`pill pill--${k.tone}`}>{k.label}</span>;
    },
    exportValue: (r) => KIND[r.movement_kind]?.label ?? r.movement_kind,
  },
  { id: 'productCode', label: 'Código', render: (r) => r.product_code, width: '8rem' },
  { id: 'productName', label: 'Artículo', render: (r) => r.product_name },
  { id: 'warehouseName', label: 'Almacén', render: (r) => r.warehouse_name },
  {
    id: 'quantity',
    label: 'Cantidad',
    numeric: true,
    render: (r) => quantity(r.quantity, r.unit),
    exportValue: (r) => r.quantity,
  },
  {
    id: 'lot',
    label: 'Lote',
    width: '6rem',
    render: (r) =>
      r.lot_id ? <span className="pill pill--info">✓ Sí</span> : <span className="due--none">—</span>,
    exportValue: (r) => (r.lot_id ? 'Sí' : ''),
  },
  {
    id: 'workOrderNumber',
    label: 'Orden',
    render: (r) => r.work_order_number ?? '—',
    width: '11rem',
  },
  { id: 'customerName', label: 'Cliente', render: (r) => r.customer_name ?? '—' },
];

const COLUMN_IDS = COLUMNS.map((c) => c.id);

const QUICK = [{ id: 'all', label: 'Todos', param: {} }] as const;

export function MovementsScreen(): React.ReactNode {
  const [params] = useSearchParams();
  const quick = QUICK.find((q) => q.id === params.get('v')) ?? QUICK[0];

  const listing = useListing<Row>({
    path: '/inventory/movements',
    columnIds: COLUMN_IDS,
    extraParams: quick.param,
    preserve: ['v'],
  });

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Movimientos</h1>
        {listing.total !== null && <span className="screen__count">{listing.total} movimientos</span>}
      </header>

      {listing.error !== null && (
        <p className="screen__error" role="alert">
          {listing.error}
        </p>
      )}

      <DataTable
        columns={COLUMNS}
        rows={listing.rows}
        idOf={(r) => r.id}
        state={listing.state}
        onStateChange={listing.setState}
        exportName="movimientos"
        loading={listing.loading}
        hasMore={listing.hasMore}
        onLoadMore={listing.loadMore}
        totalMatching={listing.total}
        emptyMessage="No hay movimientos registrados."
      />
    </section>
  );
}
